import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { trainingService } from '../services/trainingService';
import { formatDateDisplay } from '../utils/dateUtils';

function employeeDisplayName(row) {
  const name = [row.first_name, row.last_name].filter(Boolean).join(' ').trim();
  const num = row.employee_number != null && String(row.employee_number).trim() !== ''
    ? ` (#${row.employee_number})`
    : '';
  return `${name || '—'}${num}`;
}

function supervisorDisplayName(row) {
  if (!row.employee_supervisor_id) return '—';
  const name = [row.employee_supervisor_first_name, row.employee_supervisor_last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return name || '—';
}

const TrainingSignoffs = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await trainingService.listPendingSupervisor();
      setItems(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load sign-offs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1.5 }}>
        Training Sign-Offs
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employee</TableCell>
                <TableCell>Supervisor / lead</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Employee signed</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">No pending sign-offs.</TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {row.employee_id ? (
                        <Link
                          component={RouterLink}
                          to={`/hr?employee_id=${row.employee_id}`}
                          variant="inherit"
                          underline="hover"
                          sx={{ fontWeight: 400, fontSize: 'inherit' }}
                        >
                          {employeeDisplayName(row)}
                        </Link>
                      ) : (
                        employeeDisplayName(row)
                      )}
                    </TableCell>
                    <TableCell>{supervisorDisplayName(row)}</TableCell>
                    <TableCell>{row.document_title}</TableCell>
                    <TableCell>
                      {row.employee_signed_at ? formatDateDisplay(row.employee_signed_at) : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="contained" onClick={() => navigate(`/employee/training/${row.id}`)}>
                        Review & sign off
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default TrainingSignoffs;
